import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../layout/Layout';
import Header from '../../components/Header';
import { 
  Table, 
  Card, 
  Select, 
  DatePicker, 
  Button, 
  Space, 
  Tag, 
  Statistic, 
  Row, 
  Col, 
  Input,
  Spin,
  message,
  Tabs,
  Modal,
  Typography,
  Tooltip,
  Badge
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  PrinterOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  EyeOutlined,
  DollarOutlined,
  CreditCardOutlined,
  BankOutlined,
  ShoppingCartOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import fetchData from '../../functions/fetchData';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const { TabPane } = Tabs;

const CollectionSummary = () => {
  const navigate = useNavigate();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [collectionData, setCollectionData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [viewMode, setViewMode] = useState('all'); // 'all', 'paid', 'pending'
  const [paymentMode, setPaymentMode] = useState('all'); // 'all', 'cash', 'card', 'btransfer'
  const [dateRange, setDateRange] = useState([null, null]);
  const [searchText, setSearchText] = useState('');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [showTableView, setShowTableView] = useState(false);
  const [tableWiseData, setTableWiseData] = useState([]);
  const [tableBillsModalVisible, setTableBillsModalVisible] = useState(false);
  const [selectedTableBills, setSelectedTableBills] = useState(null);
  const [itemDetails, setItemDetails] = useState([]);
  const [itemDetailsLoading, setItemDetailsLoading] = useState(false);
  const [currentPageSize, setCurrentPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Summary statistics
  const [statistics, setStatistics] = useState({
    totalAmount: 0,
    paidAmount: 0,
    balanceAmount: 0,
    cashAmount: 0,
    cardAmount: 0,
    btransferAmount: 0,
    totalRecords: 0,
    paidRecords: 0,
    pendingRecords: 0
  });

  // Authentication check
  const userType = (localStorage.getItem('usertype') || sessionStorage.getItem('usertype') || '').toLowerCase();
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  
  const isAuthenticated = !!token;
  const isCashier =  userType === 'admin' || userType === 'account' || userType === 'modifier';

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (!isCashier) {
      message.error('Access denied. Only authorized users can access this page.');
      navigate('/dashboard/admin');
      return;
    }

    fetchCollectionData();
  }, [isAuthenticated, isCashier, navigate]);

  // Fetch collection data
  const fetchCollectionData = async () => {
    setLoading(true);
    try {
      const data = await fetchData('collection', null, 'id',{});
      
      if (data && data.length > 0) {
        setCollectionData(data);
        setFilteredData(data);
        calculateStatistics(data);
        message.success(`Loaded ${data.length} collection records`);
      } else {
        setCollectionData([]);
        setFilteredData([]);
        message.info('No collection data found');
      }
    } catch (error) {
      console.error('Error fetching collection data:', error);
      message.error('Failed to load collection data');
    } finally {
      setLoading(false);
    }
  };

  // Calculate statistics
  const calculateStatistics = (data) => {
    const stats = {
      totalAmount: 0,
      paidAmount: 0,
      balanceAmount: 0,
      cashAmount: 0,
      cardAmount: 0,
      btransferAmount: 0,
      totalRecords: data.length,
      paidRecords: 0,
      pendingRecords: 0
    };

    data.forEach(record => {
      const netAmount = parseFloat(record.netamount || 0);
      const paidAmt = parseFloat(record.paidamount || 0);
      const balance = parseFloat(record.balance || 0);
      const paymentMode = record.mode?.toLowerCase();
      
      // Get individual payment method amounts from separate columns
      const cardAmt = parseFloat(record.card || 0);
      const btransferAmt = parseFloat(record.btransfer || 0);

      stats.totalAmount += netAmount;
      stats.paidAmount += paidAmt;
      stats.balanceAmount += balance;
      
      // Add payment amounts:
      // 1. If mode matches, add paidamount
      // 2. Always add the specific payment column amount (card, btransfer)
      if (paymentMode === 'card') {
        stats.cardAmount += paidAmt;
      } else if (paymentMode === 'btransfer') {
        stats.btransferAmount += paidAmt;
      } else {
        // Default to cash for 'cash' mode or any other/unknown mode
        stats.cashAmount += paidAmt;
      }
      
      // Add specific payment column amounts
      stats.cardAmount += cardAmt;
      stats.btransferAmount += btransferAmt;

      // balance = 0 means PAID, balance > 0 means PENDING
      if (balance === 0) {
        stats.paidRecords++;
      } else {
        stats.pendingRecords++;
      }
    });

    setStatistics(stats);
  };

  // Filter data based on criteria
  useEffect(() => {
    let filtered = [...collectionData];

    // Filter by view mode (paid/pending)
    // balance = 0 means PAID, balance > 0 means PENDING
    if (viewMode === 'paid') {
      filtered = filtered.filter(record => parseFloat(record.balance || 0) === 0);
    } else if (viewMode === 'pending') {
      filtered = filtered.filter(record => parseFloat(record.balance || 0) > 0);
    }

    // Filter by payment mode
    if (paymentMode !== 'all') {
      filtered = filtered.filter(record => record.mode === paymentMode);
    }

    // Filter by date range using dte column
    if (dateRange[0] && dateRange[1]) {
      filtered = filtered.filter(record => {
        const recordDate = dayjs(record.dte);
        return recordDate.isSameOrAfter(dateRange[0].startOf('day')) && 
               recordDate.isSameOrBefore(dateRange[1].endOf('day'));
      });
    }

    // Filter by search text
    if (searchText) {
      filtered = filtered.filter(record => 
        record.barbill?.toString().toLowerCase().includes(searchText.toLowerCase()) ||
        record.userid?.toString().toLowerCase().includes(searchText.toLowerCase())
      );
    }

    setFilteredData(filtered);
    calculateStatistics(filtered);
  }, [viewMode, paymentMode, dateRange, searchText, collectionData]);

  // Table columns
  const columns = [
    {
      title: 'Table No',
      dataIndex: 'tables',
      key: 'tables',
      width: 100,
      fixed: 'left',
      render: (text) => <Tag color="purple">{text || '-'}</Tag>,
      sorter: (a, b) => (a.tables || '').localeCompare(b.tables || ''),
    },
    {
      title: 'Waiter No',
      dataIndex: 'waitor',
      key: 'waitor',
      width: 100,
      fixed: 'left',
      render: (text) => <Tag color="green">{text || '-'}</Tag>,
      sorter: (a, b) => (a.waiter || '').localeCompare(b.waiter || ''),
    },
    {
      title: 'Bill No.',
      dataIndex: 'barbill',
      key: 'barbill',
      width: 120,
      fixed: 'left',
      render: (text) => <Text strong style={{ color: '#1890ff' }}>{text}</Text>,
      sorter: (a, b) => (a.barbill || '').localeCompare(b.barbill || ''),
    },
    {
      title: 'Date',
      dataIndex: 'dte',
      key: 'dte',
      width: 110,
      render: (text) => dayjs(text).format('DD/MM/YYYY'),
      sorter: (a, b) => dayjs(a.dte).unix() - dayjs(b.dte).unix(),
    },
    {
      title: 'Time',
      dataIndex: 'tme',
      key: 'tme',
      width: 100,
      render: (text) => text ? text.split('.')[0] : '-',
    },
    {
      title: 'Net Amount',
      dataIndex: 'netamount',
      key: 'netamount',
      width: 120,
      align: 'right',
      render: (amount) => (
        <Text strong>฿{parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</Text>
      ),
      sorter: (a, b) => parseFloat(a.netamount || 0) - parseFloat(b.netamount || 0),
    },
    {
      title: 'Payment Mode',
      dataIndex: 'mode',
      key: 'mode',
      width: 130,
      render: (mode) => {
        const modeConfig = {
          cash: { icon: <DollarOutlined />, color: 'success' },
          card: { icon: <CreditCardOutlined />, color: 'processing' },
          btransfer: { icon: <BankOutlined />, color: 'cyan' },
        };
        
        const config = modeConfig[mode] || { icon: <ClockCircleOutlined />, color: 'default' };
        
        return mode ? (
          <Tag icon={config.icon} color={config.color}>
            {mode.toUpperCase()}
          </Tag>
        ) : (
          <Tag icon={<ClockCircleOutlined />} color="default">PENDING</Tag>
        );
      },
      filters: [
        { text: 'Cash', value: 'cash' },
        { text: 'Card', value: 'card' },
        { text: 'Bank Transfer', value: 'btransfer' },
      ],
      onFilter: (value, record) => record.mode === value,
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (_, record) => {
        const balance = parseFloat(record.balance || 0);
        // balance = 0 means PAID, balance > 0 means PENDING
        return balance === 0 ? (
          <Tag icon={<CheckCircleOutlined />} color="success">PAID</Tag>
        ) : (
          <Tag icon={<ClockCircleOutlined />} color="warning">PENDING</Tag>
        );
      },
      filters: [
        { text: 'Paid', value: 'paid' },
        { text: 'Pending', value: 'pending' },
      ],
      onFilter: (value, record) => {
        const balance = parseFloat(record.balance || 0);
        // balance = 0 means PAID, balance > 0 means PENDING
        return value === 'paid' ? balance === 0 : balance > 0;
      },
    },
    {
      title: 'Action',
      key: 'action',
      width: 80,
      fixed: 'right',
      render: (_, record) => (
        <Tooltip title="View Details">
          <Button 
            type="primary" 
            size="small" 
            icon={<EyeOutlined />}
            onClick={() => showRecordDetails(record)}
          />
        </Tooltip>
      ),
    },
  ];

  // Calculate table-wise pending summary
  const calculateTableWiseSummary = () => {
    // Filter only pending bills (balance > 0)
    const pendingBills = collectionData.filter(record => parseFloat(record.balance || 0) > 0);
    
    // Group by table
    const tableGroups = {};
    pendingBills.forEach(record => {
      const tableName = record.tables || 'Unknown';
      if (!tableGroups[tableName]) {
        tableGroups[tableName] = {
          tableName,
          bills: [],
          totalAmount: 0,
          totalBalance: 0,
          billCount: 0
        };
      }
      tableGroups[tableName].bills.push(record);
      tableGroups[tableName].totalAmount += parseFloat(record.netamount || 0);
      tableGroups[tableName].totalBalance += parseFloat(record.balance || 0);
      tableGroups[tableName].billCount++;
    });
    
    // Convert to array and sort by balance (highest first)
    const tableArray = Object.values(tableGroups).sort((a, b) => b.totalBalance - a.totalBalance);
    setTableWiseData(tableArray);
  };

  // Toggle table view
  const toggleTableView = () => {
    if (!showTableView) {
      calculateTableWiseSummary();
    }
    setShowTableView(!showTableView);
  };

  // Show record details in modal
  const showRecordDetails = async (record) => {
    setSelectedRecord(record);
    setModalVisible(true);
    setItemDetailsLoading(true);
    
    try {
      // Format the bill date to match database format
      const billDate = dayjs(record.dte).format('YYYY-MM-DD');
      
      // Fetch items from both bar_cart and food_cart in parallel
      // Filter by invoice number (numeric field) AND date to get only items from this specific bill
      const [barCartItems, foodCartItems] = await Promise.all([
        fetchData('bar_cart', null, 'id', `invoice=${record.barbill} AND dte='${billDate}'`),
        fetchData('food_cart', null, 'id', `invoice=${record.barbill} AND dte='${billDate}'`)
      ]);
      
      console.log('Fetching items for bill:', record.barbill, 'Date:', billDate);
      console.log('Bar cart items:', barCartItems);
      console.log('Food cart items:', foodCartItems);
      
      // Combine and format items with multiple field name fallbacks
      const allItems = [
        ...(barCartItems || []).map(item => ({
          ...item,
          type: 'Bar',
          itemName: item.item_name || item.item || item.itemname || item.name || '-',
          qty: parseFloat(item.quantity || item.qty || 0),
          rate: parseFloat(item.rate || item.price || 0),
          amount: parseFloat(item.amount || item.total || 0),
          date: item.dte || record.dte,
          time: item.tme || record.tme
        })),
        ...(foodCartItems || []).map(item => ({
          ...item,
          type: 'Food',
          itemName: item.item_name || item.item || item.itemname || item.name || '-',
          qty: parseFloat(item.quantity || item.qty || 0),
          rate: parseFloat(item.rate || item.price || 0),
          amount: parseFloat(item.amount || item.total || 0),
          date: item.dte || record.dte,
          time: item.tme || record.tme
        }))
      ];
      
      console.log('Total items loaded:', allItems.length);
      setItemDetails(allItems);
    } catch (error) {
      console.error('Error fetching item details:', error);
      message.error('Failed to load item details');
      setItemDetails([]);
    } finally {
      setItemDetailsLoading(false);
    }
  };

  // Show table bills in modal
  const showTableBills = (tableData) => {
    setSelectedTableBills(tableData);
    setTableBillsModalVisible(true);
  };

  // Thermal print for table bills
  const handleTableBillsThermalPrint = (tableData) => {
    const printWindow = window.open('', '_blank');
    const thermalContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Table ${tableData.tableName} Bills</title>
        <style>
          @media print {
            @page { margin: 0; }
            body { margin: 0; }
          }
          body {
            font-family: 'Courier New', monospace;
            width: 80mm;
            margin: 0 auto;
            padding: 10px;
            font-size: 12px;
          }
          .header {
            text-align: center;
            font-weight: bold;
            margin-bottom: 10px;
            border-bottom: 2px dashed #000;
            padding-bottom: 5px;
          }
          .title {
            font-size: 14px;
            margin-bottom: 5px;
          }
          .info {
            font-size: 10px;
            margin-bottom: 5px;
          }
          .bill-row {
            display: flex;
            justify-content: space-between;
            padding: 3px 0;
            border-bottom: 1px dotted #ccc;
            font-size: 11px;
          }
          .bill-no {
            font-weight: bold;
            flex: 1;
          }
          .date {
            flex: 1;
            text-align: center;
          }
          .amount {
            text-align: right;
            min-width: 60px;
          }
          .total-row {
            font-weight: bold;
            margin-top: 5px;
            padding-top: 5px;
            border-top: 2px solid #000;
            font-size: 12px;
          }
          .footer {
            text-align: center;
            margin-top: 10px;
            font-size: 10px;
            border-top: 2px dashed #000;
            padding-top: 5px;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="title">TABLE ${tableData.tableName} - PENDING BILLS</div>
          <div class="info">${dayjs().format('DD/MM/YYYY HH:mm')}</div>
          <div class="info">Total Bills: ${tableData.billCount}</div>
        </div>
        ${tableData.bills.map(bill => `
          <div class="bill-row">
            <span class="bill-no">#${bill.barbill}</span>
            <span class="date">${dayjs(bill.dte).format('DD/MM')} ${bill.tme || ''}</span>
            <span class="amount">${parseFloat(bill.balance || 0).toFixed(2)}</span>
          </div>
        `).join('')}
        <div class="bill-row total-row">
          <span>TOTAL BALANCE</span>
          <span class="amount">${tableData.totalBalance.toFixed(2)}</span>
        </div>
        <div class="footer">
          Thank you!
        </div>
      </body>
      </html>
    `;
    printWindow.document.write(thermalContent);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
    message.success('Thermal print initiated');
  };

  // Export to PDF
  const exportToPDF = () => {
    const doc = new jsPDF();
    
    if (showTableView) {
      // Export Table-wise Summary
      doc.setFontSize(18);
      doc.text('Table-wise Pending Bills Summary', 14, 20);
      
      doc.setFontSize(11);
      doc.text(`Generated on: ${dayjs().format('DD/MM/YYYY HH:mm')}`, 14, 30);
      doc.text(`Total Tables: ${tableWiseData.length}`, 14, 37);
      
      const tableData = tableWiseData.map(record => [
        record.tableName,
        record.billCount.toString(),
        parseFloat(record.totalAmount || 0).toFixed(2),
        parseFloat(record.totalBalance || 0).toFixed(2)
      ]);
      
      // Add totals row
      const totalBills = tableWiseData.reduce((sum, item) => sum + item.billCount, 0);
      const totalAmount = tableWiseData.reduce((sum, item) => sum + item.totalAmount, 0);
      const totalBalance = tableWiseData.reduce((sum, item) => sum + item.totalBalance, 0);
      
      tableData.push([
        'TOTAL',
        totalBills.toString(),
        totalAmount.toFixed(2),
        totalBalance.toFixed(2)
      ]);
      
      doc.autoTable({
        head: [['Table', 'Pending Bills', 'Total Amount', 'Total Balance']],
        body: tableData,
        startY: 45,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [102, 126, 234] },
        footStyles: { fillColor: [240, 242, 245], textColor: [0, 0, 0], fontStyle: 'bold' },
      });
      
      doc.save(`table-wise-pending-summary-${dayjs().format('YYYY-MM-DD')}.pdf`);
    } else {
      // Export Bill-wise Summary
      doc.setFontSize(18);
      doc.text('Collection Summary Report', 14, 20);
      
      doc.setFontSize(11);
      doc.text(`Generated on: ${dayjs().format('DD/MM/YYYY HH:mm')}`, 14, 30);
      doc.text(`Total Records: ${filteredData.length}`, 14, 37);
      
      const tableData = filteredData.map(record => [
        record.barbill,
        dayjs(record.dte).format('DD/MM/YYYY'),
        parseFloat(record.netamount || 0).toFixed(2),
        parseFloat(record.paidamount || 0).toFixed(2),
        parseFloat(record.balance || 0).toFixed(2),
        record.mode?.toUpperCase() || 'PENDING',
        parseFloat(record.balance || 0) === 0 ? 'PAID' : 'PENDING'
      ]);
      
      doc.autoTable({
        head: [['Bill No.', 'Date', 'Net Amount', 'Paid Amount', 'Balance', 'Mode', 'Status']],
        body: tableData,
        startY: 45,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [22, 160, 133] },
      });
      
      doc.save(`collection-summary-${dayjs().format('YYYY-MM-DD')}.pdf`);
    }
    
    message.success('PDF exported successfully');
  };

  // Export to Excel
  const exportToExcel = () => {
    const excelData = filteredData.map(record => ({
      'Bill No.': record.barbill,
      'Date': dayjs(record.dte).format('DD/MM/YYYY'),
      'Time': record.tme ? record.tme.split('.')[0] : '-',
      'Net Amount': parseFloat(record.netamount || 0).toFixed(2),
      'Paid Amount': parseFloat(record.paidamount || 0).toFixed(2),
      'Balance': parseFloat(record.balance || 0).toFixed(2),
      'Cash': parseFloat(record.paidamount || 0).toFixed(2),
      'Card': parseFloat(record.card || 0).toFixed(2),
      'Bank Transfer': parseFloat(record.btransfer || 0).toFixed(2),
      'Discount': parseFloat(record.discount || 0).toFixed(2),
      'Payment Mode': record.mode?.toUpperCase() || 'PENDING',
      'User': record.userid || '-',
      'Status': parseFloat(record.balance || 0) <= 0 ? 'PAID' : 'PENDING'
    }));

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Collection Summary');
    
    XLSX.writeFile(wb, `collection-summary-${dayjs().format('YYYY-MM-DD')}.xlsx`);
    message.success('Excel exported successfully');
  };

  // Thermal Print report
  const handleThermalPrint = () => {
    const printWindow = window.open('', '_blank');
    
    if (showTableView) {
      // Thermal print for table-wise summary
      const thermalContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Table-wise Summary</title>
          <style>
            @media print {
              @page { margin: 0; }
              body { margin: 0; }
            }
            body {
              font-family: 'Courier New', monospace;
              width: 80mm;
              margin: 0 auto;
              padding: 10px;
              font-size: 12px;
            }
            .header {
              text-align: center;
              font-weight: bold;
              margin-bottom: 10px;
              border-bottom: 2px dashed #000;
              padding-bottom: 5px;
            }
            .title {
              font-size: 14px;
              margin-bottom: 5px;
            }
            .info {
              font-size: 10px;
              margin-bottom: 5px;
            }
            .table-row {
              display: flex;
              justify-content: space-between;
              padding: 3px 0;
              border-bottom: 1px dotted #ccc;
            }
            .table-name {
              font-weight: bold;
              flex: 1;
            }
            .amount {
              text-align: right;
              min-width: 80px;
            }
            .total-row {
              font-weight: bold;
              margin-top: 5px;
              padding-top: 5px;
              border-top: 2px solid #000;
            }
            .footer {
              text-align: center;
              margin-top: 10px;
              font-size: 10px;
              border-top: 2px dashed #000;
              padding-top: 5px;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="title">TABLE-WISE PENDING BILLS</div>
            <div class="info">${dayjs().format('DD/MM/YYYY HH:mm')}</div>
          </div>
          ${tableWiseData.map(table => `
            <div class="table-row">
              <span class="table-name">TABLE ${table.tableName}</span>
            </div>
            <div class="table-row">
              <span>Bills: ${table.billCount}</span>
              <span class="amount">${table.totalBalance.toFixed(2)}</span>
            </div>
          `).join('')}
          <div class="table-row total-row">
            <span>TOTAL (${tableWiseData.length} Tables)</span>
            <span class="amount">${tableWiseData.reduce((sum, t) => sum + t.totalBalance, 0).toFixed(2)}</span>
          </div>
          <div class="footer">
            Thank you!
          </div>
        </body>
        </html>
      `;
      printWindow.document.write(thermalContent);
    } else {
      // Thermal print for bill-wise summary
      const thermalContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Collection Summary</title>
          <style>
            @media print {
              @page { margin: 0; }
              body { margin: 0; }
            }
            body {
              font-family: 'Courier New', monospace;
              width: 80mm;
              margin: 0 auto;
              padding: 10px;
              font-size: 12px;
            }
            .header {
              text-align: center;
              font-weight: bold;
              margin-bottom: 10px;
              border-bottom: 2px dashed #000;
              padding-bottom: 5px;
            }
            .title {
              font-size: 14px;
              margin-bottom: 5px;
            }
            .info {
              font-size: 10px;
              margin-bottom: 5px;
            }
            .bill-row {
              display: flex;
              justify-content: space-between;
              padding: 3px 0;
              border-bottom: 1px dotted #ccc;
            }
            .bill-no {
              font-weight: bold;
            }
            .amount {
              text-align: right;
              min-width: 80px;
            }
            .total-row {
              font-weight: bold;
              margin-top: 5px;
              padding-top: 5px;
              border-top: 2px solid #000;
            }
            .footer {
              text-align: center;
              margin-top: 10px;
              font-size: 10px;
              border-top: 2px dashed #000;
              padding-top: 5px;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="title">COLLECTION SUMMARY</div>
            <div class="info">${dayjs().format('DD/MM/YYYY HH:mm')}</div>
            <div class="info">Total Bills: ${filteredData.length}</div>
          </div>
          ${filteredData.map(record => `
            <div class="bill-row">
              <span class="bill-no">#${record.barbill}</span>
              <span class="amount">${parseFloat(record.netamount || 0).toFixed(2)}</span>
            </div>
          `).join('')}
          <div class="bill-row total-row">
            <span>TOTAL</span>
            <span class="amount">${filteredData.reduce((sum, r) => sum + parseFloat(r.netamount || 0), 0).toFixed(2)}</span>
          </div>
          <div class="footer">
            Thank you!
          </div>
        </body>
        </html>
      `;
      printWindow.document.write(thermalContent);
    }
    
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
    message.success('Thermal print initiated');
  };

  return (
    <Layout>
      <Header title="📊 Collection Summary Report" />
      <div >
      <Card 
        bordered={false}
        style={{ 
          marginBottom: '24px',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          boxShadow: '0 8px 16px rgba(102, 126, 234, 0.1)'
        }}
      >
        <Title level={2} style={{ 
          marginBottom: '24px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
          <ShoppingCartOutlined style={{ marginRight: '12px', color: '#667eea' }} />
          Collection Summary
        </Title>

        {/* Summary Statistics */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card style={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.2)'
            }}>
              <Statistic
                title={<span style={{ color: '#fff' }}>Total Amount</span>}
                value={statistics.totalAmount}
                precision={2}
                prefix="฿"
                valueStyle={{ color: '#fff', fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card style={{ 
              background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
              boxShadow: '0 4px 12px rgba(56, 239, 125, 0.2)'
            }}>
              <Statistic
                title={<span style={{ color: '#fff' }}>Paid Amount</span>}
                value={statistics.cashAmount + statistics.cardAmount + statistics.btransferAmount}
                precision={2}
                prefix="฿"
                valueStyle={{ color: '#fff', fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card style={{ 
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              boxShadow: '0 4px 12px rgba(245, 87, 108, 0.2)'
            }}>
              <Statistic
                title={<span style={{ color: '#fff' }}>Balance Amount</span>}
                value={statistics.balanceAmount}
                precision={2}
                prefix="฿"
                valueStyle={{ color: '#fff', fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card style={{ 
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              boxShadow: '0 4px 12px rgba(79, 172, 254, 0.2)'
            }}>
              <Statistic
                title={<span style={{ color: '#fff' }}>Total Records</span>}
                value={statistics.totalRecords}
                suffix={<span style={{ fontSize: '12px', color: '#fff' }}>{`(${statistics.paidRecords} paid, ${statistics.pendingRecords} pending)`}</span>}
                valueStyle={{ color: '#fff', fontWeight: 'bold' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Payment Mode Statistics */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={8} md={8}>
            <Card size="small" style={{ 
              background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
              boxShadow: '0 3px 10px rgba(168, 237, 234, 0.3)'
            }}>
              <Statistic
                title={<span style={{ color: '#2d3748' }}>Cash Payments</span>}
                value={statistics.cashAmount}
                precision={2}
                prefix={<DollarOutlined style={{ color: '#52c41a' }} />}
                valueStyle={{ color: '#2d3748', fontSize: '18px', fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8} md={8}>
            <Card size="small" style={{ 
              background: 'linear-gradient(135deg, #fbc2eb 0%, #a6c1ee 100%)',
              boxShadow: '0 3px 10px rgba(251, 194, 235, 0.3)'
            }}>
              <Statistic
                title={<span style={{ color: '#2d3748' }}>Card Payments</span>}
                value={statistics.cardAmount}
                precision={2}
                prefix={<CreditCardOutlined style={{ color: '#1890ff' }} />}
                valueStyle={{ color: '#2d3748', fontSize: '18px', fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8} md={8}>
            <Card size="small" style={{ 
              background: 'linear-gradient(135deg, #fdcbf1 0%, #e6dee9 100%)',
              boxShadow: '0 3px 10px rgba(253, 203, 241, 0.3)'
            }}>
              <Statistic
                title={<span style={{ color: '#2d3748' }}>Bank Transfers</span>}
                value={statistics.btransferAmount}
                precision={2}
                prefix={<BankOutlined style={{ color: '#13c2c2' }} />}
                valueStyle={{ color: '#2d3748', fontSize: '18px', fontWeight: 'bold' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Space wrap style={{ marginBottom: '16px', width: '100%' }}>
          <Select
            style={{ width: 150 }}
            placeholder="View Mode"
            value={viewMode}
            onChange={setViewMode}
          >
            <Select.Option value="all">All Records</Select.Option>
            <Select.Option value="paid">Paid Only</Select.Option>
            <Select.Option value="pending">Pending Only</Select.Option>
          </Select>

          <Select
            style={{ width: 150 }}
            placeholder="Payment Mode"
            value={paymentMode}
            onChange={setPaymentMode}
          >
            <Select.Option value="all">All Modes</Select.Option>
            <Select.Option value="cash">Cash</Select.Option>
            <Select.Option value="card">Card</Select.Option>
            <Select.Option value="btransfer">Bank Transfer</Select.Option>
          </Select>

          <RangePicker
            format="DD/MM/YYYY"
            onChange={(dates) => setDateRange(dates || [null, null])}
            style={{ width: 250 }}
          />

          <Input
            placeholder="Search by Bill No. or User"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 250 }}
            allowClear
          />

          <Button 
            icon={<ReloadOutlined />} 
            onClick={fetchCollectionData}
            loading={loading}
          >
            Refresh
          </Button>

          <Button 
            type={showTableView ? 'primary' : 'default'}
            onClick={toggleTableView}
          >
            {showTableView ? 'Bill View' : 'Table View'}
          </Button>

          <Button 
            icon={<FilePdfOutlined />} 
            onClick={exportToPDF}
            type="primary"
            danger
          >
            Export PDF
          </Button>

          <Button 
            icon={<FileExcelOutlined />} 
            onClick={exportToExcel}
            style={{ background: '#52c41a', borderColor: '#52c41a', color: 'white' }}
          >
            Export Excel
          </Button>

          <Button 
            icon={<PrinterOutlined />} 
            onClick={handleThermalPrint}
          >
            Thermal Print
          </Button>
        </Space>
      </Card>

      {/* Table-wise Pending Bills Summary */}
      {showTableView && (
        <Card bordered={false} style={{ 
          marginBottom: '24px',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          boxShadow: '0 8px 16px rgba(102, 126, 234, 0.1)'
        }}>
          <Title level={4} style={{ marginBottom: '16px', color: '#667eea' }}>
            Table-wise Pending Bills Summary
          </Title>
          <Table
            columns={[
              {
                title: 'Table No.',
                dataIndex: 'tableName',
                key: 'tableName',
                width: 120,
                render: (text) => <Text strong style={{ color: '#1890ff', fontSize: '16px' }}>Table {text}</Text>,
                sorter: (a, b) => a.tableName.localeCompare(b.tableName),
              },
              {
                title: 'Pending Bills',
                dataIndex: 'billCount',
                key: 'billCount',
                width: 120,
                align: 'center',
                render: (count) => <Badge count={count} style={{ backgroundColor: '#faad14' }} />,
                sorter: (a, b) => a.billCount - b.billCount,
              },
              {
                title: 'Total Amount',
                dataIndex: 'totalAmount',
                key: 'totalAmount',
                width: 150,
                align: 'right',
                render: (amount) => (
                  <Text strong style={{ color: '#1890ff' }}>
                    ฿{parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </Text>
                ),
                sorter: (a, b) => a.totalAmount - b.totalAmount,
              },
              {
                title: 'Total Balance',
                dataIndex: 'totalBalance',
                key: 'totalBalance',
                width: 150,
                align: 'right',
                render: (balance) => (
                  <Text strong style={{ color: '#f5222d', fontSize: '16px' }}>
                    ฿{parseFloat(balance).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </Text>
                ),
                sorter: (a, b) => a.totalBalance - b.totalBalance,
              },
              {
                title: 'Action',
                key: 'action',
                width: 100,
                render: (_, record) => (
                  <Button 
                    type="link" 
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={() => showTableBills(record)}
                  >
                    View Bills
                  </Button>
                ),
              },
            ]}
            dataSource={tableWiseData}
            rowKey="tableName"
            loading={loading}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} tables with pending bills`,
            }}
            scroll={{ x: 800 }}
            size="small"
            summary={(pageData) => {
              const totalBills = pageData.reduce((sum, item) => sum + item.billCount, 0);
              const totalAmount = pageData.reduce((sum, item) => sum + item.totalAmount, 0);
              const totalBalance = pageData.reduce((sum, item) => sum + item.totalBalance, 0);
              return (
                <Table.Summary.Row style={{ background: '#f0f2f5' }}>
                  <Table.Summary.Cell index={0} colSpan={2}>
                    <Text strong style={{ fontSize: '15px', fontWeight: 'bold' }}>TOTAL ({pageData.length} Tables)</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="center">
                    <Text strong style={{ fontSize: '14px', fontWeight: 'bold' }}>{totalBills}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right">
                    <Text strong style={{ color: '#1890ff', fontSize: '15px', fontWeight: 'bold' }}>
                      ฿{totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="right">
                    <Text strong style={{ color: '#f5222d', fontSize: '16px', fontWeight: 'bold' }}>
                      ฿{totalBalance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5} />
                </Table.Summary.Row>
              );
            }}
          />
        </Card>
      )}

      {/* Data Table */}
      {!showTableView && (
      <Card bordered={false} style={{ 
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        boxShadow: '0 8px 16px rgba(102, 126, 234, 0.1)'
      }}>
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1400 }}
          pagination={{
            current: currentPage,
            pageSize: currentPageSize,
            total: filteredData.length,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: [10, 20, 50, 100, 200, 500, filteredData.length],
            showTotal: (total, range) => (
              <Space>
                <span>{`${range[0]}-${range[1]} of ${total} records`}</span>
                <Button 
                  size="small" 
                  type={currentPageSize === filteredData.length ? 'primary' : 'default'}
                  onClick={() => {
                    setCurrentPageSize(filteredData.length);
                    setCurrentPage(1);
                  }}
                >
                  View All
                </Button>
              </Space>
            ),
            onChange: (page, pageSize) => {
              setCurrentPage(page);
              setCurrentPageSize(pageSize);
            },
            onShowSizeChange: (current, size) => {
              setCurrentPage(1);
              setCurrentPageSize(size);
            },
          }}
          size="small"
          summary={(pageData) => {
            const totalAmount = pageData.reduce((sum, record) => sum + parseFloat(record.netamount || 0), 0);
            return (
              <Table.Summary fixed>
                <Table.Summary.Row style={{ backgroundColor: '#fafafa', fontWeight: 'bold' }}>
                  <Table.Summary.Cell index={0} colSpan={5}>
                    <Text strong style={{ fontSize: '14px' }}>TOTAL</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5} align="right">
                    <Text strong style={{ fontSize: '14px', color: '#1890ff' }}>
                      ฿{totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6} colSpan={2} />
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      </Card>
      )}

      {/* Detail Modal */}
      <Modal
        title={`Bill Details - ${selectedRecord?.barbill || ''}`}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setSelectedRecord(null);
        }}
        footer={[
          <Button key="close" onClick={() => setModalVisible(false)}>
            Close
          </Button>,
          <Button 
            key="print" 
            type="primary" 
            icon={<PrinterOutlined />}
            onClick={() => {
              const printWindow = window.open('', '_blank');
              const thermalContent = `
                <!DOCTYPE html>
                <html>
                <head>
                  <title>Bill Details</title>
                  <style>
                    @media print {
                      @page { margin: 0; }
                      body { margin: 0; }
                    }
                    body {
                      font-family: 'Courier New', monospace;
                      width: 80mm;
                      margin: 0 auto;
                      padding: 10px;
                      font-size: 12px;
                    }
                    .header {
                      text-align: center;
                      font-weight: bold;
                      margin-bottom: 10px;
                      border-bottom: 2px dashed #000;
                      padding-bottom: 5px;
                    }
                    .row {
                      display: flex;
                      justify-content: space-between;
                      padding: 3px 0;
                    }
                    .label { font-weight: bold; }
                    .divider {
                      border-top: 1px dashed #000;
                      margin: 5px 0;
                    }
                    .items-header {
                      font-weight: bold;
                      margin-top: 10px;
                      margin-bottom: 5px;
                      text-align: center;
                      border-top: 2px solid #000;
                      border-bottom: 1px solid #000;
                      padding: 5px 0;
                    }
                    .item-row {
                      display: flex;
                      justify-content: space-between;
                      padding: 2px 0;
                      font-size: 11px;
                      border-bottom: 1px dotted #ccc;
                    }
                    .item-name {
                      flex: 2;
                      padding-right: 5px;
                    }
                    .item-qty {
                      flex: 1;
                      text-align: center;
                    }
                    .item-rate {
                      flex: 1;
                      text-align: right;
                      padding-right: 5px;
                    }
                    .item-amount {
                      flex: 1;
                      text-align: right;
                      font-weight: bold;
                    }
                    .item-type {
                      font-size: 10px;
                      color: #666;
                      font-style: italic;
                    }
                    .items-total {
                      font-weight: bold;
                      margin-top: 5px;
                      padding-top: 5px;
                      border-top: 2px solid #000;
                    }
                    .footer {
                      text-align: center;
                      margin-top: 10px;
                      border-top: 2px dashed #000;
                      padding-top: 5px;
                    }
                  </style>
                </head>
                <body>
                  <div class="header">
                    BILL DETAILS<br>
                    ${dayjs().format('DD/MM/YYYY HH:mm')}
                  </div>
                  <div class="row">
                    <span class="label">Bill No:</span>
                    <span>${selectedRecord?.barbill}</span>
                  </div>
                  <div class="row">
                    <span class="label">Table No:</span>
                    <span>${selectedRecord?.tables || '-'}</span>
                  </div>
                  <div class="row">
                    <span class="label">Waiter:</span>
                    <span>${selectedRecord?.waitor || '-'}</span>
                  </div>
                  <div class="row">
                    <span class="label">Date:</span>
                    <span>${dayjs(selectedRecord?.dte).format('DD/MM/YYYY')}</span>
                  </div>
                  <div class="row">
                    <span class="label">Time:</span>
                    <span>${selectedRecord?.tme ? selectedRecord.tme.split('.')[0] : '-'}</span>
                  </div>
                  <div class="divider"></div>
                  
                  ${itemDetails && itemDetails.length > 0 ? `
                    <div class="items-header">ITEM DETAILS</div>
                    <div class="item-row" style="font-weight: bold; border-bottom: 1px solid #000;">
                      <span class="item-name">Item</span>
                      <span class="item-qty">Qty</span>
                      <span class="item-rate">Rate</span>
                      <span class="item-amount">Amount</span>
                    </div>
                    ${itemDetails.map(item => `
                      <div class="item-row">
                        <span class="item-name">
                          ${item.itemName}<br>
                          <span class="item-type">${item.type}</span>
                        </span>
                        <span class="item-qty">${item.qty}</span>
                        <span class="item-rate">${parseFloat(item.rate || 0).toFixed(2)}</span>
                        <span class="item-amount">${parseFloat(item.amount || 0).toFixed(2)}</span>
                      </div>
                    `).join('')}
                    <div class="item-row items-total">
                      <span class="item-name">SUBTOTAL</span>
                      <span class="item-qty"></span>
                      <span class="item-rate"></span>
                      <span class="item-amount">${itemDetails.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0).toFixed(2)}</span>
                    </div>
                    <div class="divider"></div>
                  ` : ''}
                  
                  <div class="row">
                    <span class="label">Net Amount:</span>
                    <span>${parseFloat(selectedRecord?.netamount || 0).toFixed(2)}</span>
                  </div>
                  <div class="row">
                    <span class="label">Discount:</span>
                    <span>${parseFloat(selectedRecord?.discount || 0).toFixed(2)}</span>
                  </div>
                  <div class="row">
                    <span class="label">Paid Amount:</span>
                    <span>${parseFloat(selectedRecord?.paidamount || 0).toFixed(2)}</span>
                  </div>
                  <div class="row">
                    <span class="label">Balance:</span>
                    <span>${parseFloat(selectedRecord?.balance || 0).toFixed(2)}</span>
                  </div>
                  <div class="divider"></div>
                  <div class="row">
                    <span class="label">Payment Mode:</span>
                    <span>${selectedRecord?.mode?.toUpperCase() || 'PENDING'}</span>
                  </div>
                  <div class="row">
                    <span class="label">Status:</span>
                    <span>${parseFloat(selectedRecord?.balance || 0) === 0 ? 'PAID' : 'PENDING'}</span>
                  </div>
                  <div class="footer">Thank you!</div>
                </body>
                </html>
              `;
              printWindow.document.write(thermalContent);
              printWindow.document.close();
              setTimeout(() => {
                printWindow.print();
                printWindow.close();
              }, 250);
              message.success('Thermal print initiated');
            }}
          >
            Thermal Print
          </Button>
        ]}
        width={600}
      >
        {selectedRecord && (
          <div>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Text strong>Bill Number:</Text>
                <div><Text>{selectedRecord.barbill}</Text></div>
              </Col>
              <Col span={12}>
                <Text strong>Date & Time:</Text>
                <div>
                  <Text>{dayjs(selectedRecord.dte).format('DD/MM/YYYY')}</Text>
                  {selectedRecord.tme && <Text type="secondary"> {selectedRecord.tme.split('.')[0]}</Text>}
                </div>
              </Col>
              <Col span={12}>
                <Text strong>Net Amount:</Text>
                <div><Text style={{ fontSize: '16px', color: '#1890ff' }}>
                  ฿{parseFloat(selectedRecord.netamount || 0).toFixed(2)}
                </Text></div>
              </Col>
              <Col span={12}>
                <Text strong>Paid Amount:</Text>
                <div><Text style={{ fontSize: '16px', color: '#52c41a' }}>
                  ฿{parseFloat(selectedRecord.paidamount || 0).toFixed(2)}
                </Text></div>
              </Col>
              <Col span={12}>
                <Text strong>Balance:</Text>
                <div><Text style={{ fontSize: '16px', color: '#faad14' }}>
                  ฿{parseFloat(selectedRecord.balance || 0).toFixed(2)}
                </Text></div>
              </Col>
              <Col span={12}>
                <Text strong>Discount:</Text>
                <div><Text>฿{parseFloat(selectedRecord.discount || 0).toFixed(2)}</Text></div>
              </Col>
              <Col span={12}>
                <Text strong>Card Payment:</Text>
                <div><Text>฿{parseFloat(selectedRecord.card || 0).toFixed(2)}</Text></div>
              </Col>
              <Col span={12}>
                <Text strong>Bank Transfer:</Text>
                <div><Text>฿{parseFloat(selectedRecord.btransfer || 0).toFixed(2)}</Text></div>
              </Col>
              <Col span={12}>
                <Text strong>Payment Mode:</Text>
                <div>
                  {selectedRecord.mode ? (
                    <Tag color={
                      selectedRecord.mode === 'cash' ? 'success' :
                      selectedRecord.mode === 'card' ? 'processing' : 'cyan'
                    }>
                      {selectedRecord.mode.toUpperCase()}
                    </Tag>
                  ) : (
                    <Tag>PENDING</Tag>
                  )}
                </div>
              </Col>
              <Col span={12}>
                <Text strong>User:</Text>
                <div><Tag color="blue">{selectedRecord.userid || '-'}</Tag></div>
              </Col>
              <Col span={24}>
                <Text strong>Status:</Text>
                <div>
                  {parseFloat(selectedRecord.balance || 0) <= 0 ? (
                    <Tag icon={<CheckCircleOutlined />} color="success" style={{ fontSize: '14px' }}>
                      PAID
                    </Tag>
                  ) : (
                    <Tag icon={<ClockCircleOutlined />} color="warning" style={{ fontSize: '14px' }}>
                      PENDING
                    </Tag>
                  )}
                </div>
              </Col>
            </Row>
            
            {/* Item Details Section */}
            <div style={{marginTop: '20px'}}>
              <Text strong style={{fontSize: '16px', display: 'block', marginBottom: '12px'}}>
                Item Details:
              </Text>
              {itemDetailsLoading ? (
                <div style={{textAlign: 'center', padding: '20px'}}>
                  <Spin tip="Loading items..." />
                </div>
              ) : (
                <Table
                  dataSource={itemDetails}
                  rowKey={(record, index) => `${record.type}-${index}`}
                  pagination={false}
                  size="small"
                  scroll={{ x: 500 }}
                  columns={[
                    {
                      title: 'Type',
                      dataIndex: 'type',
                      key: 'type',
                      width: 80,
                      render: (type) => (
                        <Tag color={type === 'Bar' ? 'blue' : 'green'}>{type}</Tag>
                      )
                    },
                    {
                      title: 'Item Name',
                      dataIndex: 'itemName',
                      key: 'itemName',
                      width: 200,
                    },
                    {
                      title: 'Qty',
                      dataIndex: 'qty',
                      key: 'qty',
                      width: 60,
                      align: 'center',
                    },
                    {
                      title: 'Rate',
                      dataIndex: 'rate',
                      key: 'rate',
                      width: 100,
                      align: 'right',
                      render: (rate) => `฿${parseFloat(rate || 0).toFixed(2)}`
                    },
                    {
                      title: 'Amount',
                      dataIndex: 'amount',
                      key: 'amount',
                      width: 100,
                      align: 'right',
                      render: (amount) => (
                        <Text strong>฿{parseFloat(amount || 0).toFixed(2)}</Text>
                      )
                    },
                    {
                      title: 'Date',
                      dataIndex: 'date',
                      key: 'date',
                      width: 100,
                      render: (date) => dayjs(date).format('DD/MM/YYYY')
                    },
                    {
                      title: 'Time',
                      dataIndex: 'time',
                      key: 'time',
                      width: 90,
                      render: (time) => time ? time.split('.')[0] : '-'
                    },
                  ]}
                  summary={(pageData) => {
                    const totalQty = pageData.reduce((sum, record) => sum + parseFloat(record.qty || 0), 0);
                    const totalAmount = pageData.reduce((sum, record) => sum + parseFloat(record.amount || 0), 0);
                    return (
                      <Table.Summary.Row style={{ backgroundColor: '#fafafa', fontWeight: 'bold' }}>
                        <Table.Summary.Cell index={0} colSpan={2}>
                          <Text strong>TOTAL</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={2} align="center">
                          <Text strong>{totalQty}</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={3} />
                        <Table.Summary.Cell index={4} align="right">
                          <Text strong style={{ color: '#1890ff' }}>
                            ฿{totalAmount.toFixed(2)}
                          </Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={5} colSpan={2} />
                      </Table.Summary.Row>
                    );
                  }}
                />
              )}
              {!itemDetailsLoading && itemDetails.length === 0 && (
                <div style={{textAlign: 'center', padding: '20px', color: '#999'}}>
                  No items found for this bill
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Table Bills Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>
              <Badge 
                count={selectedTableBills?.tableName || ''} 
                style={{ backgroundColor: '#1890ff', marginRight: 10 }} 
              />
              Table {selectedTableBills?.tableName} - Pending Bills
            </span>
            <Tag color="red" style={{ fontSize: '14px', marginLeft: 10 }}>
              {selectedTableBills?.billCount} Bills
            </Tag>
          </div>
        }
        open={tableBillsModalVisible}
        onCancel={() => setTableBillsModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setTableBillsModalVisible(false)}>
            Close
          </Button>,
          <Button 
            key="print" 
            type="primary" 
            icon={<PrinterOutlined />}
            onClick={() => handleTableBillsThermalPrint(selectedTableBills)}
          >
            Thermal Print
          </Button>
        ]}
        width={900}
      >
        {selectedTableBills && (
          <div>
            {/* Summary Cards */}
            <Row gutter={16} style={{ marginBottom: 20 }}>
              <Col span={8}>
                <Card size="small" style={{ 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white'
                }}>
                  <Statistic
                    title={<span style={{ color: 'white' }}>Total Bills</span>}
                    value={selectedTableBills.billCount}
                    valueStyle={{ color: 'white', fontWeight: 'bold' }}
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small" style={{ 
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  color: 'white'
                }}>
                  <Statistic
                    title={<span style={{ color: 'white' }}>Total Amount</span>}
                    value={selectedTableBills.totalAmount}
                    precision={2}
                    prefix="฿"
                    valueStyle={{ color: 'white', fontWeight: 'bold' }}
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small" style={{ 
                  background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                  color: 'white'
                }}>
                  <Statistic
                    title={<span style={{ color: 'white' }}>Total Balance</span>}
                    value={selectedTableBills.totalBalance}
                    precision={2}
                    prefix="฿"
                    valueStyle={{ color: 'white', fontWeight: 'bold' }}
                  />
                </Card>
              </Col>
            </Row>

            {/* Bills Table */}
            <Table
              columns={[
                {
                  title: 'Bill No.',
                  dataIndex: 'barbill',
                  key: 'barbill',
                  width: 100,
                  render: (text) => <Text strong style={{ color: '#1890ff' }}>#{text}</Text>,
                },
                {
                  title: 'Date',
                  dataIndex: 'dte',
                  key: 'dte',
                  width: 120,
                  render: (date) => dayjs(date).format('DD/MM/YYYY'),
                },
                {
                  title: 'Time',
                  dataIndex: 'tme',
                  key: 'tme',
                  width: 100,
                  render: (time) => time ? time.split('.')[0] : '-',
                },
                {
                  title: 'Net Amount',
                  dataIndex: 'netamount',
                  key: 'netamount',
                  width: 120,
                  align: 'right',
                  render: (amount) => (
                    <Text strong style={{ color: '#1890ff' }}>
                      ฿{parseFloat(amount || 0).toFixed(2)}
                    </Text>
                  ),
                },
                {
                  title: 'Paid Amount',
                  dataIndex: 'paidamount',
                  key: 'paidamount',
                  width: 120,
                  align: 'right',
                  render: (amount) => (
                    <Text style={{ color: '#52c41a' }}>
                      ฿{parseFloat(amount || 0).toFixed(2)}
                    </Text>
                  ),
                },
                {
                  title: 'Balance',
                  dataIndex: 'balance',
                  key: 'balance',
                  width: 120,
                  align: 'right',
                  render: (balance) => (
                    <Text strong style={{ color: '#f5222d', fontSize: '15px' }}>
                      ฿{parseFloat(balance || 0).toFixed(2)}
                    </Text>
                  ),
                },
                {
                  title: 'Payment Mode',
                  dataIndex: 'mode',
                  key: 'mode',
                  width: 120,
                  render: (mode) => {
                    if (!mode) return <Tag>PENDING</Tag>;
                    const modeColors = {
                      cash: 'success',
                      card: 'processing',
                      btransfer: 'cyan'
                    };
                    return <Tag color={modeColors[mode] || 'default'}>{mode.toUpperCase()}</Tag>;
                  },
                },
                {
                  title: 'Action',
                  key: 'action',
                  width: 80,
                  render: (_, record) => (
                    <Tooltip title="View Details">
                      <Button 
                        type="link" 
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => {
                          setTableBillsModalVisible(false);
                          showRecordDetails(record);
                        }}
                      />
                    </Tooltip>
                  ),
                },
              ]}
              dataSource={selectedTableBills.bills}
              rowKey="id"
              pagination={false}
              size="small"
              scroll={{ y: 300 }}
              summary={(pageData) => {
                const totalNet = pageData.reduce((sum, item) => sum + parseFloat(item.netamount || 0), 0);
                const totalPaid = pageData.reduce((sum, item) => sum + parseFloat(item.paidamount || 0), 0);
                const totalBalance = pageData.reduce((sum, item) => sum + parseFloat(item.balance || 0), 0);
                return (
                  <Table.Summary.Row style={{ background: '#f0f2f5' }}>
                    <Table.Summary.Cell index={0} colSpan={3}>
                      <Text strong style={{ fontSize: '14px', fontWeight: 'bold' }}>TOTAL</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                      <Text strong style={{ color: '#1890ff', fontSize: '14px', fontWeight: 'bold' }}>
                        ฿{totalNet.toFixed(2)}
                      </Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="right">
                      <Text strong style={{ color: '#52c41a', fontSize: '14px', fontWeight: 'bold' }}>
                        ฿{totalPaid.toFixed(2)}
                      </Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={5} align="right">
                      <Text strong style={{ color: '#f5222d', fontSize: '15px', fontWeight: 'bold' }}>
                        ฿{totalBalance.toFixed(2)}
                      </Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={6} colSpan={2} />
                  </Table.Summary.Row>
                );
              }}
            />
          </div>
        )}
      </Modal>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
      />
      </div>
    </Layout>
  );
};

export default CollectionSummary;
